import React, { useMemo } from "react";
import {
  MergeDataIndex,
  MergeDataNode,
  MergeDataSearch,
} from "../utils/mergeDataIndex";
import MergeDataRow from "./MergeDataRow";
import { useStyles } from "../styles";

interface Props {
  index: MergeDataIndex;
  expandedIds: Set<string>;
  onToggle: (id: string) => void;
  onCopy: (node: MergeDataNode) => void;
  search: MergeDataSearch | null;
}

// Every descendant (any depth) of a matched node -- a node whose ANY
// ancestor matches, not just its direct parent. Without this, expanding a
// child of a matched container shows an empty group: grandchildren that
// don't themselves match get filtered out.
const descendantsOfMatches = (
  index: MergeDataIndex,
  search: MergeDataSearch | null
): Set<string> => {
  const result = new Set<string>();
  if (!search) return result;

  const visit = (id: string) => {
    index.nodes[id]?.childIds.forEach((childId) => {
      if (!result.has(childId)) {
        result.add(childId);
        visit(childId);
      }
    });
  };
  search.matchIds.forEach(visit);
  return result;
};

const MergeDataTree = ({
  index,
  expandedIds,
  onToggle,
  onCopy,
  search,
}: Props) => {
  const styles = useStyles();
  const query = search?.query ?? "";

  const descendantIds = useMemo(
    () => descendantsOfMatches(index, search),
    [index, search]
  );

  const isVisible = (node: MergeDataNode): boolean =>
    !search ||
    search.matchIds.has(node.id) ||
    search.ancestorIds.has(node.id) ||
    descendantIds.has(node.id);

  const renderNodes = (ids: string[]): React.ReactNode =>
    ids
      .map((id) => index.nodes[id])
      .filter(isVisible)
      .map((node) => {
        const expanded = node.kind !== "value" && expandedIds.has(node.id);
        return (
          <li
            key={node.id}
            role="treeitem"
            aria-expanded={node.kind === "value" ? undefined : expanded}
          >
            <MergeDataRow
              node={node}
              expanded={expanded}
              query={query}
              styles={styles}
              onToggle={onToggle}
              onCopy={onCopy}
            />
            {expanded && <ul role="group">{renderNodes(node.childIds)}</ul>}
          </li>
        );
      });

  return (
    <ul role="tree" className={styles.tree}>
      {renderNodes(index.rootIds)}
    </ul>
  );
};

export default React.memo(MergeDataTree);
